import { MASK, normalizeBlobMask } from './tileset-source.js';

const SKY_COLOR = '#87ceeb';
const EMPTY_INTERIOR = new Set();

export class Renderer {
  constructor(ctx, displayTileSize, manifest, atlas) {
    this.ctx = ctx;
    this.tileSize = displayTileSize;
    this.tiles = new Map(manifest.tiles.map((tile) => [tile.id, tile]));
    this.backdrop = manifest.tiles.find((tile) => tile.channel === 'backdrop');
    this.cliff = manifest.tiles.find((tile) => tile.channel === 'cliff');
    this.transitions = new Map(manifest.tiles.filter((tile) => tile.channel === 'transition')
      .map((tile) => [tile.transitionKind, tile]));
    this.heightOverlay = manifest.tiles.find((tile) => tile.channel === 'heightOverlay');
    this.atlas = atlas;
    this.showCollision = false;
    this.showHeight = false;
    this.staticTreeCanvases = new Map();
    this.decorationInteractions = new Map();
    this._surfaceChains = new Map();
    this._closestMaskTables = new WeakMap();
    this._interiorMemo = { world: null, startX: NaN, startY: NaN, cells: null };
    this._frameNow = 0;
    this._backdropPatterns = [];
    this._backdropPatternSize = 0;
    // Upper bound for sweeping stale interaction entries: past the longest
    // interaction any entry is spent, even if its cell scrolled away or its
    // tile was erased before the per-draw cleanup could run.
    this.maxInteractionMs = Math.max(
      0, ...manifest.tiles.map((tile) => tile.animation?.interactionDurationMs ?? 0),
    );
    this.ctx.imageSmoothingEnabled = false;
  }

  _maskFor(tile, connects) {
    if (tile.mode === 'plain') return 0;
    let mask = 0;
    if (connects(0, -1)) mask |= MASK.N;
    if (connects(1, 0)) mask |= MASK.E;
    if (connects(0, 1)) mask |= MASK.S;
    if (connects(-1, 0)) mask |= MASK.W;
    if (tile.mode === 'blob8') {
      if (connects(1, -1)) mask |= MASK.NE;
      if (connects(1, 1)) mask |= MASK.SE;
      if (connects(-1, 1)) mask |= MASK.SW;
      if (connects(-1, -1)) mask |= MASK.NW;
      mask = normalizeBlobMask(mask);
    }
    return mask;
  }

  _storedMask(world, x, y, channel, tile) {
    return this._maskFor(tile, (dx, dy) => {
      const neighbor = this.tiles.get(world.getLayer(x + dx, y + dy, channel));
      return neighbor?.connectionGroup === tile.connectionGroup;
    });
  }

  _effectiveSurfaceId(world, x, y) {
    const explicitSurface = world.getLayer(x, y, 'surface');
    if (explicitSurface) return explicitSurface;
    const foundation = this.tiles.get(world.getLayer(x, y, 'foundation'));
    return foundation?.defaultSurfaceId ?? 0;
  }

  _surfaceChain(surfaceId) {
    const cached = this._surfaceChains.get(surfaceId);
    if (cached) return cached;
    const chain = [];
    const visited = new Set();
    let tile = this.tiles.get(surfaceId);
    while (tile && tile.channel === 'surface' && !visited.has(tile.id)) {
      chain.push(tile);
      visited.add(tile.id);
      tile = this.tiles.get(tile.parentSurfaceId);
    }
    chain.reverse();
    this._surfaceChains.set(surfaceId, chain);
    return chain;
  }

  _surfaceContains(world, x, y, ancestorId) {
    return this._surfaceChain(this._effectiveSurfaceId(world, x, y))
      .some((surface) => surface.id === ancestorId);
  }

  _coordinateVariant(x, y, count) {
    let value = Math.imul(x, 0x1f123bb5) ^ Math.imul(y, 0x5f356495);
    value ^= value >>> 16;
    return Math.abs(value) % count;
  }

  triggerDecoration(x, y, now = performance.now()) {
    this.decorationInteractions.set(`${x},${y}`, now);
  }

  _drawFrame(tile, component, mask, screenX, screenY, animationVariant = 0, interactionAge = Infinity, now = this._frameNow) {
    const animation = tile.animation?.component === component ? tile.animation : null;
    const interacting = interactionAge < (animation?.interactionDurationMs ?? 0);
    const duration = interacting ? animation.interactionFrameDurationMs : animation?.frameDurationMs;
    const animationIndex = animation ? Math.floor(now / duration)
      % animation.frames.length : 0;
    const frames = animation?.kind === 'variants'
      ? animation.frames[animationIndex]
      : tile.components[component];
    const resolvedMask = this._closestMask(frames, mask);
    const frame = animation
      ? animation.kind === 'variants'
        ? animation.frames[animationIndex][resolvedMask]
        : animation.frames[animationIndex][animationVariant]
      : frames?.[resolvedMask];
    if (!frame) return;
    this.ctx.drawImage(
      this.atlas,
      frame.x, frame.y, frame.w, frame.h,
      screenX, screenY, this.tileSize, this.tileSize,
    );
  }

  _closestMaskTable(frames) {
    let table = this._closestMaskTables.get(frames);
    if (table) return table;
    const masks = Object.keys(frames).map(Number);
    table = new Uint8Array(256);
    if (masks.length) {
      const bitCount = (value) => { let count = 0; for (; value; value >>>= 1) count += value & 1; return count; };
      for (let wanted = 0; wanted < 256; wanted++) {
        if (frames[wanted]) {
          table[wanted] = wanted;
          continue;
        }
        table[wanted] = masks.reduce((best, candidate) =>
          bitCount(candidate ^ wanted) < bitCount(best ^ wanted) ? candidate : best, masks[0]);
      }
    }
    this._closestMaskTables.set(frames, table);
    return table;
  }

  _closestMask(frames, wanted) {
    if (!frames || frames[wanted]) return wanted;
    if (wanted < 0 || wanted > 255) return wanted;
    return this._closestMaskTable(frames)[wanted];
  }

  _drawStored(world, x, y, channel, component, screenX, screenY, now = this._frameNow) {
    const tile = this.tiles.get(world.getLayer(x, y, channel));
    if (!tile) return;
    const variant = tile.mode === 'random' ? this._coordinateVariant(x, y, tile.randomVariantCount) : 0;
    let mask = tile.mode === 'random' ? variant : this._storedMask(world, x, y, channel, tile);
    if (tile.mode === 'footprint') {
      const placement = world.getPlacementRecord(world.getPlacement(x, y, channel));
      if (!placement) return;
      const dx = x - placement.x; const dy = placement.y - y;
      mask = dy * tile.footprint.width + dx;
    }
    const cellKey = `${x},${y}`;
    const interactionTime = this.decorationInteractions.get(cellKey);
    const interactionAge = interactionTime === undefined ? Infinity : now - interactionTime;
    if (interactionAge >= (tile.animation?.interactionDurationMs ?? Infinity)) {
      this.decorationInteractions.delete(cellKey);
    }
    this._drawFrame(tile, component, mask, screenX, screenY, variant, interactionAge, now);
  }

  _drawTree(world, x, y, screenX, screenY) {
    const tile = this.tiles.get(world.getLayer(x, y, 'treeWood'));
    const sprite = tile?.staticTreeSprite;
    if (!sprite) return;
    let canvas = this.staticTreeCanvases.get(tile.id);
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.width = sprite.w;
      canvas.height = sprite.h;
      const context = canvas.getContext('2d');
      context.imageSmoothingEnabled = false;
      context.drawImage(this.atlas, sprite.x, sprite.y, sprite.w, sprite.h, 0, 0, sprite.w, sprite.h);
      this.staticTreeCanvases.set(tile.id, canvas);
    }
    const scale = this.tileSize / 16;
    this.ctx.drawImage(
      canvas,
      screenX - (sprite.w - 16) * scale / 2,
      screenY - (sprite.h - 16) * scale,
      sprite.w * scale,
      sprite.h * scale,
    );
  }

  interiorCellsFor(world, actorPosition) {
    return this._interiorCellsFor(world, actorPosition);
  }

  hasSouthFacade(world, x, y) {
    return this._hasSouthFacade(world, x, y);
  }

  _interiorCellsFor(world, actorPosition) {
    if (!actorPosition) return EMPTY_INTERIOR;
    const startX = Math.floor(actorPosition.x);
    const startY = Math.floor(actorPosition.y + 0.38);
    const memo = this._interiorMemo;
    if (memo.cells && memo.world === world && memo.startX === startX && memo.startY === startY) {
      return memo.cells;
    }
    const cells = this._computeInteriorCells(world, startX, startY);
    memo.world = world;
    memo.startX = startX;
    memo.startY = startY;
    memo.cells = cells;
    return cells;
  }

  _computeInteriorCells(world, startX, startY) {
    if (!this._exteriorAt(world, startX, startY)) return new Set();
    const visible = new Set([`${startX},${startY}`]);
    const pending = [[startX, startY]];
    while (pending.length) {
      const [x, y] = pending.pop();
      for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
        const nx = x + dx; const ny = y + dy; const key = `${nx},${ny}`;
        if (visible.has(key) || !this._exteriorAt(world, nx, ny)) continue;
        visible.add(key);
        pending.push([nx, ny]);
      }
    }
    return visible;
  }

  _exteriorAt(world, x, y) {
    const tile = this.tiles.get(world.getLayer(x, y, 'exterior'));
    return tile?.shell ? tile : null;
  }

  _hasSouthFacade(world, x, y, exterior = this._exteriorAt(world, x, y)) {
    if (!exterior?.components.onGround) return false;
    const south = this._exteriorAt(world, x, y + 1);
    return south?.connectionGroup !== exterior.connectionGroup;
  }

  _drawExteriorFacade(world, x, y, screenX, screenY) {
    const exterior = this._exteriorAt(world, x, y);
    if (!this._hasSouthFacade(world, x, y, exterior)) return;
    let mask = 0;
    for (const [dx, direction] of [[-1, MASK.W], [1, MASK.E]]) {
      const neighbor = this._exteriorAt(world, x + dx, y);
      if (neighbor?.connectionGroup === exterior.connectionGroup
        && this._hasSouthFacade(world, x + dx, y, neighbor)) mask |= direction;
    }
    this._drawFrame(exterior, 'onGround', mask, screenX, screenY + this.tileSize);
  }

  _roofAt(world, x, y) {
    const tile = this.tiles.get(world.getLayer(x, y, 'roof'));
    return tile?.components.eave ? tile : null;
  }

  _hasEave(world, x, y, roof = this._roofAt(world, x, y)) {
    if (!roof) return false;
    const south = this._roofAt(world, x, y + 1);
    return south?.connectionGroup !== roof.connectionGroup;
  }

  /** A roof's front edge hangs one cell down over the facade below it. */
  _drawRoofEave(world, x, y, screenX, screenY) {
    const roof = this._roofAt(world, x, y);
    if (!this._hasEave(world, x, y, roof)) return;
    let mask = 0;
    for (const [dx, direction] of [[-1, MASK.W], [1, MASK.E]]) {
      const neighbor = this._roofAt(world, x + dx, y);
      if (neighbor?.connectionGroup === roof.connectionGroup
        && this._hasEave(world, x + dx, y, neighbor)) mask |= direction;
    }
    this._drawFrame(roof, 'eave', mask, screenX, screenY + this.tileSize);
  }

  _drawFoundationGround(world, x, y, screenX, screenY) {
    const tile = this.tiles.get(world.getLayer(x, y, 'foundation'));
    if (!tile) return;
    // Coastline artwork is exclusively a level-zero land/water transition.
    // Raised terrain gets a solid top; the cliff overlay owns its exposed edge.
    const mask = world.getElevation(x, y) > 0 && tile.mode === 'blob8'
      ? normalizeBlobMask(255)
      : this._storedMask(world, x, y, 'foundation', tile);
    this._drawFrame(tile, 'ground', mask, screenX, screenY);
  }

  _drawSurface(world, x, y, component, screenX, screenY) {
    for (const surface of this._surfaceChain(this._effectiveSurfaceId(world, x, y))) {
      const mask = this._maskFor(surface, (dx, dy) =>
        this._surfaceContains(world, x + dx, y + dy, surface.id));
      this._drawFrame(surface, component, mask, screenX, screenY);
    }
  }

  _backdropPattern(animationIndex) {
    if (this._backdropPatternSize !== this.tileSize) {
      this._backdropPatterns = [];
      this._backdropPatternSize = this.tileSize;
    }
    const cached = this._backdropPatterns[animationIndex];
    if (cached) return cached;
    const frame = this.backdrop.animation.frames[animationIndex][0];
    const tile = document.createElement('canvas');
    tile.width = this.tileSize;
    tile.height = this.tileSize;
    const tileCtx = tile.getContext('2d');
    tileCtx.imageSmoothingEnabled = false;
    tileCtx.drawImage(
      this.atlas, frame.x, frame.y, frame.w, frame.h,
      0, 0, this.tileSize, this.tileSize,
    );
    const pattern = this.ctx.createPattern(tile, 'repeat');
    this._backdropPatterns[animationIndex] = pattern;
    return pattern;
  }

  _fillBackdrop(origin, canvasW, canvasH, now) {
    const animation = this.backdrop.animation;
    const gridSize = animation.gridSize ?? 1;
    if (gridSize !== 1) {
      const minX = Math.floor(-origin.x / this.tileSize) - 1;
      const minY = Math.floor(-origin.y / this.tileSize) - 1;
      const maxX = Math.ceil((canvasW - origin.x) / this.tileSize) + 1;
      const maxY = Math.ceil((canvasH - origin.y) / this.tileSize) + 1;
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const variantX = ((x % gridSize) + gridSize) % gridSize;
          const variantY = ((y % gridSize) + gridSize) % gridSize;
          this._drawFrame(
            this.backdrop, 'ground', 0,
            origin.x + x * this.tileSize, origin.y + y * this.tileSize,
            variantY * gridSize + variantX, Infinity, now,
          );
        }
      }
      return;
    }
    const animationIndex = Math.floor(now / animation.frameDurationMs) % animation.frames.length;
    const pattern = this._backdropPattern(animationIndex);
    if (!pattern) return;
    const { ctx } = this;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.translate(origin.x, origin.y);
    ctx.fillStyle = pattern;
    ctx.fillRect(-origin.x, -origin.y, canvasW, canvasH);
    ctx.restore();
  }

  render(world, origin, canvasW, canvasH, drawActor = null, actorPosition = null) {
    const { ctx, tileSize } = this;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = this.backdrop?.backdropColor ?? this.backdrop?.previewColor ?? SKY_COLOR;
    ctx.fillRect(0, 0, canvasW, canvasH);

    const now = performance.now();
    this._frameNow = now;
    this._interiorMemo.cells = null;
    for (const [key, start] of this.decorationInteractions) {
      if (now - start > this.maxInteractionMs) this.decorationInteractions.delete(key);
    }

    const minX = Math.floor(-origin.x / tileSize) - 1;
    const minY = Math.floor(-origin.y / tileSize) - 1;
    const maxX = Math.ceil((canvasW - origin.x) / tileSize) + 1;
    const maxY = Math.ceil((canvasH - origin.y) / tileSize) + 1;
    const eachCellIn = (x0, y0, x1, y1, draw) => {
      for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
        draw(x, y, origin.x + x * tileSize, origin.y + y * tileSize);
      }
    };
    const eachVisibleCell = (draw) => eachCellIn(minX, minY, maxX, maxY, draw);
    const visibleInterior = this._interiorCellsFor(world, actorPosition);
    const interiorIsVisible = (x, y) => !this._exteriorAt(world, x, y)
      || visibleInterior.has(`${x},${y}`);
    const actorSortY = actorPosition ? actorPosition.y + 0.38 : null;
    const treeIsInFront = (y) => actorSortY !== null && y + 1 > actorSortY;

    if (this.backdrop) this._fillBackdrop(origin, canvasW, canvasH, now);

    eachVisibleCell((x, y, sx, sy) => this._drawFoundationGround(world, x, y, sx, sy));
    eachVisibleCell((x, y, sx, sy) => this._drawSurface(world, x, y, 'ground', sx, sy));
    eachVisibleCell((x, y, sx, sy) => this._drawStored(world, x, y, 'outdoorFloor', 'ground', sx, sy, now));
    eachVisibleCell((x, y, sx, sy) => this._drawStored(world, x, y, 'exterior', 'wall', sx, sy, now));
    // Interior floors: always when the player is inside the shell. Also show during
    // construction when the cell has no roof yet — otherwise floors "place" with no
    // visible feedback under the solid exterior wall art.
    eachVisibleCell((x, y, sx, sy) => {
      if (!world.getLayer(x, y, 'interiorFloor')) return;
      const unroofed = !world.getLayer(x, y, 'roof');
      if (interiorIsVisible(x, y) || unroofed) {
        this._drawStored(world, x, y, 'interiorFloor', 'ground', sx, sy, now);
      }
    });

    if (this.cliff) eachVisibleCell((x, y, sx, sy) => {
      const elevation = world.getElevation(x, y);
      if (!elevation) return;
      let exposedMask = 0;
      if (world.getElevation(x, y - 1) < elevation) exposedMask |= MASK.N;
      if (world.getElevation(x + 1, y) < elevation) exposedMask |= MASK.E;
      if (world.getElevation(x, y + 1) < elevation) exposedMask |= MASK.S;
      if (world.getElevation(x - 1, y) < elevation) exposedMask |= MASK.W;
      if (exposedMask) this._drawFrame(this.cliff, 'ground', exposedMask, sx, sy);
    });

    // South-facing elevation boundaries become repeatable rock-face bands. A
    // two-level drop naturally draws two bands; terraces leave their top cells
    // visible. Faces project up to three rows south of their source, so scan
    // two extra rows past the top edge or tall cliffs pop in while scrolling.
    if (this.cliff) eachCellIn(minX, minY - 2, maxX, maxY, (x, y, sx, sy) => {
      const drop = world.getElevation(x, y) - world.getElevation(x, y + 1);
      for (let band = 1; band <= drop; band++) {
        const bandKind = drop === 1 ? 0 : band === 1 ? 1 : band === drop ? 3 : 2;
        const leftDrop = world.getElevation(x - 1, y) - world.getElevation(x - 1, y + 1);
        const rightDrop = world.getElevation(x + 1, y) - world.getElevation(x + 1, y + 1);
        const faceMask = bandKind | (leftDrop < band ? 4 : 0) | (rightDrop < band ? 8 : 0);
        this._drawFrame(this.cliff, 'wall', faceMask, sx, sy + tileSize * band);
      }
    });
    eachVisibleCell((x, y, sx, sy) => {
      const transition = world.getTransition(x, y);
      const tile = transition && this.transitions.get(transition.kind);
      if (!tile) return;
      const directionMasks = [1, 2, 4, 8];
      const orientation = directionMasks.indexOf(transition.mask);
      const parallel = transition.mask === MASK.N || transition.mask === MASK.S
        ? [[-1, 0], [1, 0]]
        : [[0, -1], [0, 1]];
      let segment = 0;
      for (let side = 0; side < 2; side++) {
        const [dx, dy] = parallel[side];
        const neighbor = world.getTransition(x + dx, y + dy);
        if (neighbor?.kind === transition.kind && neighbor.mask === transition.mask) segment |= 1 << side;
      }
      this._drawFrame(tile, 'ground', orientation * 4 + segment, sx, sy);
    });

    eachVisibleCell((x, y, sx, sy) => {
      this._drawSurface(world, x, y, 'onGround', sx, sy);
      if (interiorIsVisible(x, y)) {
        this._drawStored(world, x, y, 'onGround', 'onGround', sx, sy, now);
      }
      this._drawStored(world, x, y, 'foundation', 'onGround', sx, sy, now);
    });

    // Static tree sprites reach four tiles above and about two beside their
    // anchor, so trees just past the bottom and side edges are still visible.
    const eachTreeCell = (draw) => eachCellIn(minX - 2, minY, maxX + 2, maxY + 3, draw);
    eachTreeCell((x, y, sx, sy) => {
      if (!treeIsInFront(y)) this._drawTree(world, x, y, sx, sy);
    });

    const facadeIsInFront = (y) => actorSortY === null || y + 1 >= actorSortY;

    // Projected facades north of the actor belong behind it. Previously every
    // building face was drawn after the actor, including buildings far away.
    if (actorSortY !== null) {
      eachVisibleCell((x, y, sx, sy) => {
        if (!facadeIsInFront(y)) this._drawExteriorFacade(world, x, y, sx, sy);
      });
      eachVisibleCell((x, y, sx, sy) => {
        if (!facadeIsInFront(y) && this._hasSouthFacade(world, x, y)) {
          const wallObject = this.tiles.get(world.getLayer(x, y, 'wallObject'));
          if (wallObject?.paletteSection !== 'interiorWallObjects' || interiorIsVisible(x, y)) {
            this._drawStored(world, x, y, 'wallObject', 'wall', sx, sy + tileSize, now);
          }
        }
      });
    }

    drawActor?.();

    eachTreeCell((x, y, sx, sy) => {
      if (treeIsInFront(y)) this._drawTree(world, x, y, sx, sy);
    });

    // Like a cliff face, a building's exposed south edge projects one tile
    // toward the viewer. Draw it late enough to cover the ground below it.
    eachVisibleCell((x, y, sx, sy) => {
      if (facadeIsInFront(y)) this._drawExteriorFacade(world, x, y, sx, sy);
    });

    eachVisibleCell((x, y, sx, sy) => {
      this._drawStored(world, x, y, 'foundation', 'wall', sx, sy + tileSize, now);
      this._drawSurface(world, x, y, 'wall', sx, sy);
      const wall = this.tiles.get(world.getLayer(x, y, 'wall'));
      if (wall?.paletteSection !== 'interiorWalls' || interiorIsVisible(x, y)) {
        this._drawStored(world, x, y, 'wall', 'wall', sx, sy, now);
        if (wall?.paletteSection === 'interiorWalls') {
          const mask = this._storedMask(world, x, y, 'wall', wall);
          this._drawFrame(wall, 'roof', mask, sx, sy + tileSize);
        }
      }
      const hasFacade = this._hasSouthFacade(world, x, y);
      const wallObject = this.tiles.get(world.getLayer(x, y, 'wallObject'));
      const objectVisible = wallObject?.paletteSection !== 'interiorWallObjects' || interiorIsVisible(x, y);
      if (objectVisible && (!hasFacade || facadeIsInFront(y))) {
        this._drawStored(world, x, y, 'wallObject', 'wall', sx, hasFacade ? sy + tileSize : sy, now);
      }
    });

    eachVisibleCell((x, y, sx, sy) => {
      this._drawStored(world, x, y, 'foundation', 'roof', sx, sy, now);
      this._drawSurface(world, x, y, 'roof', sx, sy);
      if (!visibleInterior.has(`${x},${y}`)) {
        this._drawStored(world, x, y, 'roof', 'roof', sx, sy, now);
        this._drawRoofEave(world, x, y, sx, sy);
      }
    });

    if (this.showHeight && this.heightOverlay) {
      eachVisibleCell((x, y, sx, sy) => {
        const elevation = world.getElevation(x, y);
        if (elevation) this._drawFrame(this.heightOverlay, 'roof', elevation, sx, sy);
      });
    }

    if (this.showCollision) {
      eachVisibleCell((x, y, sx, sy) => {
        const override = world.getCollisionOverride(x, y);
        const blocked = world.isCollidable(x, y);
        if (!blocked && override === -1) return;
        ctx.fillStyle = override === 0 ? 'rgba(45, 220, 125, 0.38)'
          : override === 1 ? 'rgba(255, 35, 35, 0.52)'
            : 'rgba(255, 145, 35, 0.38)';
        ctx.fillRect(sx, sy, tileSize, tileSize);
      });
    }
  }
}
